import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import imageResizer from 'js/image-resizer';
import ImageSource from 'components/models/image-source';

const transforms = {
  fill: 'fill',
  fit: 'fit',
};

const retinaMultipliers = [1, 2];

const buildResizedSet = (src, width, height, transform, focusPoint) => {
  if (!width && !height) return [];

  const resizeType =
    transform === transforms.fill
      ? imageResizer.Transforms.downFill
      : imageResizer.Transforms.downFit;
  const x = focusPoint?.x ?? 0;
  const y = focusPoint?.y ?? 0;

  return retinaMultipliers.map(multiplier => {
    const resizeWidth = Math.round(width * multiplier);
    const resizeHeight = Math.round(height * multiplier);

    return {
      url: imageResizer.buildResizeUrl(
        src,
        resizeWidth,
        resizeHeight,
        resizeType,
        x,
        y
      ),
      descriptor: `${multiplier}x`,
    };
  });
};

const Image = ({
  src,
  alt = '',
  className,
  sources = [],
  fluid = false,
  fetchPriority,
  loading,
  transform = transforms.fill,
  focusPoint,
}) => {
  if (!src) return null;

  return (
    <picture
      className={cn('image', className, {
        image_fluid: fluid,
      })}
    >
      {src && imageResizer.checkImageCanBeResized(src)
        ? sources
            .sort((a, b) => a.media - b.media)
            .map(({ width, height, media, imageSrc }, index) => {
              const resizedSet = buildResizedSet(
                imageSrc ?? src,
                width,
                height,
                transform,
                focusPoint
              );
              const srcSet = resizedSet
                .map(({ url, descriptor }) => `${url} ${descriptor}`)
                .join(', ');
              return (
                <source
                  key={media || width}
                  {...(media && {
                    media:
                      index !== sources.length - 1
                        ? `(max-width: ${media}px)`
                        : sources[index - 1]?.media
                          ? `(min-width: ${sources[index - 1].media}px)`
                          : undefined,
                  })}
                  srcSet={srcSet}
                />
              );
            })
        : null}
      <img
        loading={loading}
        fetchPriority={fetchPriority}
        className="image__img"
        src={src}
        alt={alt}
      />
    </picture>
  );
};

Image.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  fluid: PropTypes.bool,
  sources: PropTypes.arrayOf(PropTypes.exact(ImageSource.propTypes)),
  focusPoint: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }),
  transform: PropTypes.oneOf(Object.values(transforms)),
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
  loading: PropTypes.oneOf(['eager', 'lazy']),
};

Image.viewModelMeta = {
  sources: 'ignore',
  fetchPriority: 'ignore',
  loading: 'ignore',
  transform: 'ignore',
  className: 'ignore',
  fluid: 'ignore',
};

export default Image;
